"use client";

/**
 * EPIC-053 Market Creation Hub — Polymarket-style price ladder for
 * multi-outcome markets with `kind_variant='price_ladder'` (schema
 * landed in migration 0157).
 *
 * Reference: Polymarket "Bitcoin May 11-17" + "Airbnb ABNB Week of
 * May 11" pages — vertical stack of bucket rows, each row showing:
 *
 *     ↑ $94,000        2%   [ Buy Yes 0.4¢ ] [ Buy No 99.8¢ ]
 *       $12,197 Vol.
 *
 * Mobile-first per the platform UX rule (CLAUDE.md): Yes/No pills
 * stack vertically below the row body at <768px, side-by-side at
 * ≥768px. Min tap target 40px per the Polymarket-embed-widget pattern.
 *
 * Read-only mode: when `onTradeIntent` is omitted the Yes/No pills
 * render as plain non-interactive chips so the component can be
 * embedded in a non-trading surface (admin preview, post body, etc.).
 */

import { useTranslation } from "react-i18next";
import { ArrowUp } from "lucide-react";
import { formatPriceCents, formatVolumeCents } from "@/lib/format";
import { useDisplayCurrency } from "@/lib/useDisplayCurrency";
import { useFxRate } from "@/lib/useFxRate";

export interface PriceLadderBucket {
  id: string;
  name: string;
  sortOrder: number;
  threshold?: number;
  label?: string;
}

export interface PriceLadderOutcome {
  id: string;
  name: string;
  currentPriceCents: number;
  volumeCents: string;
  openInterestShares: string;
}

export interface PriceLadderMarket {
  id: string;
  kindVariant: "price_ladder";
  bucketSpec: {
    outcomes: PriceLadderBucket[];
  };
  outcomes: PriceLadderOutcome[];
}

interface Props {
  market: PriceLadderMarket;
  onTradeIntent?: (outcomeId: string, side: "YES" | "NO") => void;
}

function formatBucketLabel(bucket: PriceLadderBucket): string {
  if (bucket.label) return bucket.label;
  if (
    typeof bucket.threshold === "number" &&
    Number.isFinite(bucket.threshold)
  ) {
    return `$${bucket.threshold.toLocaleString("en-US")}`;
  }
  return bucket.name;
}

export function PriceLadder({ market, onTradeIntent }: Props) {
  const { t } = useTranslation();
  // 2026-05-19 — locale-aware volume display.
  const displayCurrency = useDisplayCurrency();
  const fx = useFxRate("BRL");
  const usdcPerBrl = fx?.rate ?? null;
  const interactive = typeof onTradeIntent === "function";

  const outcomeById = new Map<string, PriceLadderOutcome>();
  for (const o of market.outcomes) {
    outcomeById.set(o.id, o);
  }

  const buckets = [...market.bucketSpec.outcomes].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );

  return (
    <div
      className="overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]"
      role="list"
      aria-label={t("priceLadder.aria", "Price ladder")}
    >
      {buckets.map((bucket, idx) => {
        const outcome = outcomeById.get(bucket.id);
        const yesCents = outcome?.currentPriceCents ?? 0;
        const noCents = 100 - yesCents;
        const probabilityPct = yesCents;
        const isBelowOnePct = probabilityPct > 0 && probabilityPct < 1;
        const isZeroProb = probabilityPct === 0;
        const rowDim = isBelowOnePct || isZeroProb || outcome === undefined;
        const probabilityLabel =
          outcome === undefined
            ? "—"
            : isBelowOnePct
              ? "<1%"
              : `${probabilityPct}%`;

        return (
          <div
            key={bucket.id}
            role="listitem"
            className={`flex flex-col gap-2 border-t border-[var(--color-border)] px-3 py-3 transition-opacity md:flex-row md:items-center md:gap-4 md:px-4 ${
              idx === 0 ? "border-t-0" : ""
            } ${rowDim ? "opacity-60" : ""}`}
          >
            <div className="flex flex-1 items-start gap-2 md:items-center">
              <ArrowUp
                size={16}
                strokeWidth={2}
                className="mt-0.5 flex-shrink-0 text-[var(--color-text3)] md:mt-0"
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[15px] font-bold tabular-nums text-[var(--color-text)] md:text-[17px]">
                  {formatBucketLabel(bucket)}
                </p>
                {outcome && (
                  <p className="text-[11px] text-[var(--color-text3)]">
                    {t("priceLadder.volume", {
                      defaultValue: "{{vol}} Vol.",
                      vol: formatVolumeCents(outcome.volumeCents, displayCurrency, usdcPerBrl),
                    })}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center md:w-16 md:justify-center">
              <span className="text-[18px] font-bold tabular-nums text-[var(--color-text)] md:text-[20px]">
                {probabilityLabel}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2 md:w-[260px] md:flex-shrink-0">
              <BuyPill
                side="YES"
                priceCents={yesCents}
                interactive={interactive && outcome !== undefined}
                onClick={
                  interactive && outcome !== undefined
                    ? () => onTradeIntent?.(outcome.id, "YES")
                    : undefined
                }
              />
              <BuyPill
                side="NO"
                priceCents={noCents}
                interactive={interactive && outcome !== undefined}
                onClick={
                  interactive && outcome !== undefined
                    ? () => onTradeIntent?.(outcome.id, "NO")
                    : undefined
                }
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface BuyPillProps {
  side: "YES" | "NO";
  priceCents: number;
  interactive: boolean;
  onClick?: () => void;
}

function BuyPill({ side, priceCents, interactive, onClick }: BuyPillProps) {
  const { t } = useTranslation();
  const colorVar = side === "YES" ? "var(--color-yes)" : "var(--color-no)";
  const label =
    side === "YES"
      ? t("priceLadder.buyYes", "Buy Yes")
      : t("priceLadder.buyNo", "Buy No");

  const sharedClass =
    "flex min-h-[40px] items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-bold transition-colors";
  const sharedStyle = {
    background: `color-mix(in srgb, ${colorVar} 12%, transparent)`,
    color: colorVar,
    borderColor: `color-mix(in srgb, ${colorVar} 30%, transparent)`,
    borderWidth: 1,
  } as const;

  if (!interactive) {
    return (
      <span
        className={sharedClass}
        style={sharedStyle}
        aria-label={`${label} ${formatPriceCents(priceCents)}`}
      >
        <span>{label}</span>
        <span className="tabular-nums">{formatPriceCents(priceCents)}</span>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${sharedClass} cursor-pointer hover:brightness-110`}
      style={sharedStyle}
      aria-label={`${label} ${formatPriceCents(priceCents)}`}
    >
      <span>{label}</span>
      <span className="tabular-nums">{formatPriceCents(priceCents)}</span>
    </button>
  );
}
